import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Smartphone, X } from "lucide-react";

const STORAGE_KEY = "link_install_banner_dismissed_at";
const DISMISS_TTL_MS = 7 * 24 * 60 * 60 * 1000; // re-show after 7 days

function isStandalone() {
  if (typeof window === "undefined" || typeof navigator === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

/**
 * Dismissible "Install Link Ride to your home screen" CTA.
 *
 * Renders nothing if:
 *   - the app is already installed (standalone display mode)
 *   - the visitor has dismissed it within the last 7 days
 *
 * Designed for first-time-customer pages (TrackingPage, PaymentSuccess) — the
 * moment of highest install intent is right after a successful booking.
 */
export default function InstallBanner({ testIdPrefix = "install-banner" }) {
  const navigate = useNavigate();
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;
    try {
      const dismissed = parseInt(localStorage.getItem(STORAGE_KEY) || "0", 10);
      if (dismissed && Date.now() - dismissed < DISMISS_TTL_MS) return;
    } catch {
      /* localStorage may be blocked — show by default */
    }
    setShow(true);
  }, []);

  if (!show) return null;

  const dismiss = (e) => {
    e?.stopPropagation();
    try { localStorage.setItem(STORAGE_KEY, String(Date.now())); } catch { /* noop */ }
    setShow(false);
  };

  return (
    <div
      className="rounded-2xl bg-gradient-to-br from-[#0047FF]/15 via-[#0a0a0a] to-[#0a0a0a] border border-[#0047FF]/40 p-4 flex items-center gap-3 cursor-pointer hover:border-[#0047FF]/70 transition-colors"
      onClick={() => navigate("/install")}
      data-testid={testIdPrefix}
    >
      <div className="p-2 rounded-xl bg-[#0047FF]/15 border border-[#0047FF]/30 shrink-0">
        <Smartphone className="w-5 h-5 text-[#0047FF]" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-bold text-white leading-tight">
          Get Link Ride on your home screen
        </p>
        <p className="text-xs text-zinc-400 mt-0.5">
          One-tap booking, push alerts when your driver arrives — 30 seconds.
        </p>
      </div>
      <button
        onClick={dismiss}
        className="p-1.5 rounded-full text-zinc-500 hover:text-white hover:bg-white/5 transition-colors shrink-0"
        aria-label="Dismiss"
        data-testid={`${testIdPrefix}-dismiss`}
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
